import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCollection, addDocument, updateDocument, deleteDocument } from '@/hooks/useFirestore';
import { auth } from '@/config/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { v4 as uuidv4 } from 'uuid';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { 
  Plus, 
  Search, 
  Users as UsersIcon,
  Shield,
  UserCog,
  Edit,
  Trash2,
  Mail,
  Building2
} from 'lucide-react';

const UserForm = ({ user, locations, onSave, onClose }) => {
  const [formData, setFormData] = useState(user || {
    name: '',
    email: '',
    password: '',
    role: 'store_manager',
    assignedLocations: []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleLocation = (locationId) => {
    const current = formData.assignedLocations || [];
    if (current.includes(locationId)) {
      setFormData({ ...formData, assignedLocations: current.filter(id => id !== locationId) });
    } else {
      setFormData({ ...formData, assignedLocations: [...current, locationId] });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="userName">Full Name *</Label>
        <Input
          id="userName"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Doe"
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="userEmail">Email *</Label>
        <Input
          id="userEmail"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@example.com"
          required
          disabled={!!user}
        />
      </div>
      {!user && (
        <div className="space-y-2">
          <Label htmlFor="userPassword">Password *</Label>
          <Input
            id="userPassword"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="••••••••"
            required={!user}
          />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="userRole">Role *</Label>
        <Select 
          value={formData.role} 
          onValueChange={(value) => setFormData({ ...formData, role: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hq_admin">HQ Admin</SelectItem>
            <SelectItem value="store_manager">Store Manager</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {formData.role === 'store_manager' && (
        <div className="space-y-2">
          <Label>Assigned Locations</Label>
          <ScrollArea className="h-40 border border-slate-700 rounded-lg p-3">
            <div className="space-y-2">
              {locations.map(loc => (
                <div key={loc.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`loc-${loc.id}`}
                    checked={(formData.assignedLocations || []).includes(loc.id)}
                    onCheckedChange={() => toggleLocation(loc.id)}
                  />
                  <label htmlFor={`loc-${loc.id}`} className="text-sm text-slate-300 cursor-pointer">
                    {loc.name} ({loc.city})
                  </label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : (user ? 'Update' : 'Create')} User
        </Button>
      </DialogFooter>
    </form>
  );
};

const Users = () => {
  const { isHQ, currentUser } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const { documents: users, loading } = useCollection('users');
  const { documents: locations } = useCollection('locations');

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleSaveUser = async (data) => {
    try {
      if (editingUser) {
        // Update existing user
        await updateDocument('users', editingUser.id, {
          name: data.name,
          role: data.role,
          assignedLocations: data.assignedLocations
        });
        toast({ title: 'User updated successfully' });
      } else {
        // Create new user with Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
        const uid = userCredential.user.uid;
        
        // Create user document in Firestore
        await setDoc(doc(db, 'users', uid), {
          id: uid,
          uid: uid,
          email: data.email,
          name: data.name,
          role: data.role,
          assignedLocations: data.assignedLocations || [],
          createdAt: serverTimestamp(),
          isOnline: false
        });
        
        toast({ title: 'User created successfully' });
      }
      setEditingUser(null);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      throw err;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.uid) {
      toast({ title: 'Cannot delete yourself', variant: 'destructive' });
      return;
    }
    await deleteDocument('users', userId);
    toast({ title: 'User deleted successfully' });
  };

  if (!isHQ) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Shield className="w-16 h-16 text-slate-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Access Restricted</h2>
        <p className="text-slate-400">Only HQ administrators can manage users</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-slate-400">Manage user accounts and permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingUser(null)}>
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit' : 'Add New'} User</DialogTitle>
              <DialogDescription>Configure user account and permissions</DialogDescription>
            </DialogHeader>
            <UserForm 
              user={editingUser} 
              locations={locations}
              onSave={handleSaveUser} 
              onClose={() => { setDialogOpen(false); setEditingUser(null); }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48 bg-slate-800/50 border-slate-700">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="hq_admin">HQ Admin</SelectItem>
            <SelectItem value="store_manager">Store Manager</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Grid */}
      {filteredUsers.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UsersIcon className="w-12 h-12 text-slate-500 mb-4" />
            <p className="text-slate-400">No users found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map(user => {
            const assignedLocationNames = (user.assignedLocations || []).map(
              id => locations.find(l => l.id === id)?.name
            ).filter(Boolean);
            
            return (
              <Card key={user.id} className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'hq_admin' ? 'bg-purple-500/20' : 'bg-indigo-500/20'}`}>
                        {user.role === 'hq_admin' ? (
                          <Shield className="w-5 h-5 text-purple-400" />
                        ) : (
                          <UserCog className="w-5 h-5 text-indigo-400" />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-white text-base">{user.name || 'Unnamed'}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge 
                        className={`${user.role === 'hq_admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-indigo-500/20 text-indigo-400'}`}
                      >
                        {user.role === 'hq_admin' ? 'HQ Admin' : 'Manager'}
                      </Badge>
                      <Badge 
                        className={`${user.isOnline ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'}`}
                      >
                        {user.isOnline ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {user.role === 'store_manager' && (
                    <div className="mb-4 p-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                        <Building2 className="w-4 h-4" />
                        <span>Assigned Locations ({assignedLocationNames.length})</span>
                      </div>
                      {assignedLocationNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {assignedLocationNames.slice(0, 3).map((name, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {name}
                            </Badge>
                          ))}
                          {assignedLocationNames.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{assignedLocationNames.length - 3} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500">No locations assigned</p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => { setEditingUser(user); setDialogOpen(true); }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    {user.id !== currentUser?.uid && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-slate-800 border-slate-700">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete User</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{user.name || user.email}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteUser(user.id)} className="bg-red-600 hover:bg-red-700">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Users;
